/* Created by Interactive 63 - https://interactive63.com */
/**
 * Keyframe and track helpers. Functions that change data are "draft mutators": they take a
 * project (normally an Immer draft inside a store update) and modify it in place, so several
 * of them compose inside a single undoable action. Query functions never modify their input.
 */
import { defaultEasing } from '../model/defaults';
import { createId } from '../model/ids';
import type {
  Easing,
  ElementId,
  Keyframe,
  Project,
  PropertyKey,
  PropertyValue,
  Track,
} from '../model/types';
import { isPropertyAllowed } from '../properties/registry';
import { cycleTime } from './repeat';

export interface KeyframeLocation {
  track: Track;
  keyframe: Keyframe;
  index: number;
}

/** Limits a number to an inclusive range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * The latest time a keyframe on this track may take: the scene end, or an explicit repeat period
 * (an automatic period grows with the keyframes, so it never limits them).
 */
export function maxKeyframeTime(project: Project, track: Track): number {
  const explicit = track.repeat?.period ?? null;
  return explicit === null ? project.scene.duration : Math.min(project.scene.duration, explicit);
}

/**
 * Sorts a track's keyframes by time and collapses keyframes that share a time, keeping the
 * later one in the original order, which is the most recent edit.
 */
function sortAndDedupe(track: Track): void {
  const sorted = [...track.keyframes].sort((a, b) => a.time - b.time);
  track.keyframes = sorted.filter((kf, i) => sorted[i + 1]?.time !== kf.time);
}

/** Removes tracks that no longer hold any keyframes. */
function pruneEmptyTracks(project: Project): void {
  project.tracks = project.tracks.filter((track) => track.keyframes.length > 0);
}

/** Returns the track for an element and property, if one exists. */
export function findTrack(
  project: Project,
  elementId: ElementId,
  property: PropertyKey,
): Track | undefined {
  return project.tracks.find((t) => t.elementId === elementId && t.property === property);
}

/** Returns the existing track for an element and property, or creates an enabled empty one. */
export function ensureTrack(project: Project, elementId: ElementId, property: PropertyKey): Track {
  const existing = findTrack(project, elementId, property);
  if (existing) return existing;
  const track: Track = {
    id: createId('tr'),
    elementId,
    property,
    keyframes: [],
    enabled: true,
  };
  project.tracks.push(track);
  // Return the stored entry: inside an Immer recipe, pushing yields a draft proxy.
  return project.tracks[project.tracks.length - 1] ?? track;
}

/**
 * Inserts a keyframe, or replaces the one at the same time. A replacement keeps the existing ID
 * and easing unless an easing is given. Times are rounded to whole milliseconds and clamped to
 * the scene duration.
 */
export function setKeyframe(
  project: Project,
  elementId: ElementId,
  property: PropertyKey,
  time: number,
  value: PropertyValue,
  easing?: Easing,
): Keyframe {
  const track = ensureTrack(project, elementId, property);
  const at = clamp(Math.round(time), 0, maxKeyframeTime(project, track));
  const existing = track.keyframes.find((kf) => kf.time === at);
  if (existing) {
    existing.value = value;
    if (easing) existing.easing = easing;
    return existing;
  }
  const keyframe: Keyframe = {
    id: createId('kf'),
    time: at,
    value,
    easing: easing ?? defaultEasing(),
  };
  track.keyframes.push(keyframe);
  track.keyframes.sort((a, b) => a.time - b.time);
  return keyframe;
}

/** Removes the given keyframes, deleting any track left empty. */
export function removeKeyframes(project: Project, keyframeIds: readonly string[]): void {
  const ids = new Set(keyframeIds);
  for (const track of project.tracks) {
    track.keyframes = track.keyframes.filter((kf) => !ids.has(kf.id));
  }
  pruneEmptyTracks(project);
}

/**
 * Moves a set of keyframes by the same delta. The group is clamped so that none leaves the scene,
 * and a moved keyframe that lands on a stationary one on the same track replaces it.
 */
export function moveKeyframes(
  project: Project,
  keyframeIds: readonly string[],
  deltaMs: number,
): void {
  const ids = new Set(keyframeIds);
  const moving = project.tracks.flatMap((t) =>
    t.keyframes
      .filter((kf) => ids.has(kf.id))
      .map((kf) => ({ kf, limit: maxKeyframeTime(project, t) })),
  );
  if (moving.length === 0) return;
  const earliest = Math.min(...moving.map(({ kf }) => kf.time));
  const room = Math.min(...moving.map(({ kf, limit }) => limit - kf.time));
  const delta = clamp(Math.round(deltaMs), -earliest, room);
  if (delta === 0) return;

  for (const track of project.tracks) {
    const movedTimes = new Set<number>();
    for (const kf of track.keyframes) {
      if (ids.has(kf.id)) {
        kf.time += delta;
        movedTimes.add(kf.time);
      }
    }
    if (movedTimes.size === 0) continue;
    track.keyframes = track.keyframes.filter((kf) => ids.has(kf.id) || !movedTimes.has(kf.time));
    track.keyframes.sort((a, b) => a.time - b.time);
  }
}

/** Sets the easing of each given keyframe. */
export function setKeyframeEasing(
  project: Project,
  keyframeIds: readonly string[],
  easing: Easing,
): void {
  const ids = new Set(keyframeIds);
  for (const track of project.tracks) {
    for (const kf of track.keyframes) {
      if (ids.has(kf.id)) kf.easing = easing;
    }
  }
}

/** Sets the value of one keyframe. */
export function setKeyframeValue(project: Project, keyframeId: string, value: PropertyValue): void {
  const found = findKeyframe(project, keyframeId);
  if (found) found.keyframe.value = value;
}

/**
 * Multiplies every keyframe time by a factor, used when the scene duration changes with
 * "scale keyframes" ticked. Keyframes that collide after rounding are merged.
 */
export function scaleKeyframeTimes(project: Project, factor: number): void {
  if (!Number.isFinite(factor) || factor <= 0) return;
  for (const track of project.tracks) {
    for (const kf of track.keyframes) kf.time = Math.round(kf.time * factor);
    sortAndDedupe(track);
  }
}

/**
 * Pulls any keyframe beyond the scene duration back to the end, merging keyframes that collide.
 * Used after the duration is shortened without scaling.
 */
export function clampKeyframesToDuration(project: Project): void {
  for (const track of project.tracks) {
    for (const kf of track.keyframes) kf.time = Math.min(kf.time, project.scene.duration);
    sortAndDedupe(track);
  }
}

/** Finds a keyframe by ID along with its track and index. */
export function findKeyframe(project: Project, keyframeId: string): KeyframeLocation | undefined {
  for (const track of project.tracks) {
    const index = track.keyframes.findIndex((kf) => kf.id === keyframeId);
    const keyframe = track.keyframes[index];
    if (keyframe) return { track, keyframe, index };
  }
  return undefined;
}

/** Returns the keyframes on an element that sit within the tolerance of a time. */
export function keyframesAt(
  project: Project,
  elementId: ElementId,
  time: number,
  tolerance = 0.5,
): Keyframe[] {
  return project.tracks
    .filter((t) => t.elementId === elementId)
    .flatMap((t) => {
      // A repeating track shows its first cycle's keyframes wherever the time falls in a cycle.
      const local = t.repeat ? cycleTime(t, time).time : time;
      return t.keyframes.filter((kf) => Math.abs(kf.time - local) <= tolerance);
    });
}

/**
 * Finds the nearest keyframe time before or after a time across the given elements, ignoring
 * keyframes at the time itself. When a property is given only its tracks are considered.
 * Returns undefined when there is none in that direction.
 */
export function adjacentKeyframeTime(
  project: Project,
  elementIds: readonly ElementId[],
  time: number,
  direction: 'previous' | 'next',
  property?: PropertyKey,
): number | undefined {
  const ids = new Set(elementIds);
  const epsilon = 0.5;
  let best: number | undefined;
  for (const track of project.tracks) {
    if (!ids.has(track.elementId) || (property !== undefined && track.property !== property))
      continue;
    for (const kf of track.keyframes) {
      if (direction === 'previous' && kf.time < time - epsilon) {
        if (best === undefined || kf.time > best) best = kf.time;
      } else if (direction === 'next' && kf.time > time + epsilon) {
        if (best === undefined || kf.time < best) best = kf.time;
      }
    }
  }
  return best;
}

/**
 * Whether a property can be added to an element: the tag must allow it, and draw and morph
 * cannot coexist on one element.
 */
export function canAddProperty(
  project: Project,
  elementId: ElementId,
  property: PropertyKey,
): boolean {
  const element = project.elements[elementId];
  if (!element || !isPropertyAllowed(property, element.tag)) return false;
  if (property === 'draw') return !findTrack(project, elementId, 'morph');
  if (property === 'morph') {
    return element.tag === 'path' && !findTrack(project, elementId, 'draw');
  }
  return true;
}
